/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
	AutoComplete,
	Input,
	Select,
	Switch,
	Drawer,
	DatePicker,
	Checkbox,
	Divider,
	message,
	Tooltip,
} from 'antd';
import {
	ExportOutlined,
	RedoOutlined,
	PlusOutlined,
	EyeOutlined,
	DownOutlined,
	CloseCircleOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { FilterTag } from 'components/common';
import {
	getProjectListV2,
	searchBusinessUnitsV2,
	exportProjectCSV,
	updateProjectV2,
} from 'store/projectSliceV2';
import { searchClientFiltersV2 } from 'store/clientSliceV2';
import { searchRouteFiltersV2, getRouteByIdV2 } from 'store/routeSliceV2';
import { usePersistedState } from 'hooks';
import { StatusChangeModal } from 'componentsv2/modals';
import { PROJECT_TYPE_OPTIONS } from 'componentsv2/forms/project/projectTypes';

const { RangePicker } = DatePicker;

const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
`;

const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const FilterHeader = styled(Flex)`
	justify-content: space-between;
	align-items: center;
	padding: 16px 0;
`;

const FilterTitleText = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #333;
`;

const FilterCloseButton = styled(CloseCircleOutlined)`
	font-size: 20px;
	color: #999;
	cursor: pointer;

	&:hover {
		color: #666;
	}
`;

const FilterSection = styled(Box)`
	margin-bottom: 16px;
`;

const FilterSectionTitle = styled(Text)`
	font-size: 14px;
	font-weight: 500;
	color: #333;
	margin-bottom: 12px;
	display: block;
`;

const DateRangeWrapper = styled(Flex)`
	gap: 12px;
`;

const StyledRangePicker = styled(RangePicker)`
	width: 100%;
	height: 40px;
`;

const CheckboxWrapper = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const StyledCheckbox = styled(Checkbox)`
	margin-left: 0 !important;

	.ant-checkbox + span {
		font-size: 14px;
		color: #333;
	}
`;

const SeeMoreLink = styled(Flex)`
	align-items: center;
	gap: 4px;
	color: #1890ff;
	cursor: pointer;
	font-size: 14px;
	margin-top: 8px;

	&:hover {
		text-decoration: underline;
	}
`;

const ResetButton = styled(FilterAtnButton)`
	width: 100%;
	margin-top: 0;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;
const mapRouteToOption = (route) => {
	const label = route?.route_code
		? `${route.route_name || 'Route'} (${route.route_code})`
		: route?.route_name || `Route #${route?.id}`;
	return { value: route?.id ?? null, label };
};
const normalizeRouteOption = (item) =>
	item && item.value !== undefined && item.label !== undefined ? item : mapRouteToOption(item);
const mergeRouteOptions = (...routeLists) => {
	const optionsMap = new Map();

	routeLists
		.flat()
		.filter(Boolean)
		.map(normalizeRouteOption)
		.forEach((route) => {
			if (route.value !== null && route.value !== undefined && route.value !== '') {
				optionsMap.set(route.value, route);
			}
		});

	return Array.from(optionsMap.values()).sort((firstRoute, secondRoute) =>
		firstRoute.label.localeCompare(secondRoute.label)
	);
};

/** project_type is a code on the wire ('warehousing'); show the same label as the form. */
const PROJECT_TYPE_LABELS = PROJECT_TYPE_OPTIONS.reduce(
	(acc, option) => ({ ...acc, [option.value]: option.label }),
	{}
);

const getTableColumns = (navigate, handleStatusToggle) => [
	{
		title: 'Project Name',
		dataIndex: 'projectName',
		key: 'projectName',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Project Type',
		dataIndex: 'projectType',
		key: 'projectType',
		render: (text) => <Text style={{ whiteSpace: 'nowrap' }}>{text || '-'}</Text>,
	},
	{
		title: 'Client Name',
		dataIndex: 'clientName',
		key: 'clientName',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Project Address',
		dataIndex: 'projectAddress',
		key: 'projectAddress',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Coordinates',
		dataIndex: 'coordinates',
		key: 'coordinates',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Business Unit',
		dataIndex: 'businessUnit',
		key: 'businessUnit',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Project Start Date',
		dataIndex: 'projectStartDate',
		key: 'projectStartDate',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'PoD Required',
		dataIndex: 'podRequired',
		key: 'podRequired',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Created at',
		dataIndex: 'createdAt',
		key: 'createdAt',
		sorter: true,
		defaultSortOrder: 'descend',
		render: (text) => (
			<Text style={{ whiteSpace: 'nowrap' }}>
				{text && text !== '-' ? moment(text).format('DD/MM/YYYY, h:mm a') : '-'}
			</Text>
		),
	},
	{
		title: 'Created by',
		dataIndex: 'createdBy',
		key: 'createdBy',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		render: (status, record) => (
			<Switch
				checked={status}
				checkedChildren="Active"
				unCheckedChildren="Inactive"
				onClick={() => handleStatusToggle(record.id, record.projectName, status)}
			/>
		),
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id) => (
			<ViewButton onClick={() => navigate(`/projects/${id}`)}>
				<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
			</ViewButton>
		),
	},
];

export default function ProjectListPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('projectList:search', '');
	const [projects, setProjects] = useState([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = usePersistedState('projectList:pagination', {
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [filterBy, setFilterBy] = useState({ business_unit_ids: [] });

	const [dateRange, setDateRange] = useState(null);
	const [statusFilters, setStatusFilters] = usePersistedState('projectList:statusFilters', {
		all: false,
		active: false,
		inactive: false,
	});
	const [businessUnits, setBusinessUnits] = useState([]);
	const [clients, setClients] = useState([]);
	const [clientNameFilter, setClientNameFilter] = usePersistedState('projectList:clientName', '');
	const [selectedBusinessUnits, setSelectedBusinessUnits] = usePersistedState(
		'projectList:businessUnits',
		[]
	);
	const [showAllBusinessUnits, setShowAllBusinessUnits] = useState(false);
	const [loadingBusinessUnits, setLoadingBusinessUnits] = useState(false);
	const [loadingClients, setLoadingClients] = useState(false);
	const [routeFilter, setRouteFilter] = usePersistedState('projectList:routeFilter', null);
	const [selectedRouteLabel, setSelectedRouteLabel] = usePersistedState(
		'projectList:routeLabel',
		''
	);
	const [routeOptions, setRouteOptions] = useState([]);
	const [loadingRoutes, setLoadingRoutes] = useState(false);
	const routeSearchTimeoutRef = useRef(null);

	// Status change modal states
	const [statusModalVisible, setStatusModalVisible] = useState(false);
	const [selectedProject, setSelectedProject] = useState(null);
	const [newStatus, setNewStatus] = useState(false);
	const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
	const appliedClientName = clientNameFilter.trim();

	// Helper function to map API project data to table format
	const mapProjectToTableRow = (project) => ({
		id: project.id,
		key: project.id,
		projectName: project.project_name || project.project_code || '-',
		// Rows created before project_type existed come back without it — they are transportation
		projectType:
			PROJECT_TYPE_LABELS[project.project_type] || PROJECT_TYPE_LABELS.transportation,
		clientName: project.client_name || '-',
		projectAddress: project.project_address || '-',
		coordinates:
			project.latitude && project.longitude
				? `${project.latitude}, ${project.longitude}`
				: '-',
		businessUnit: project.business_unit_name || '-',
		projectStartDate: project.project_start_date || '-',
		podRequired: project.pod_required ? 'Yes' : 'No',
		createdAt: project.created_at || '-',
		createdBy: project.created_by_name || '-',
		status: project.is_active !== undefined ? project.is_active : true,
	});

	// Fetch business units when filter drawer opens
	useEffect(() => {
		if (filterDrawerOpen) {
			if (businessUnits.length === 0) {
				fetchBusinessUnits();
			}
			if (clients.length === 0) {
				fetchClients();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen]);
	useEffect(() => {
		fetchRouteOptions('', routeFilter);
		return () => {
			if (routeSearchTimeoutRef.current) {
				clearTimeout(routeSearchTimeoutRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch projects on component mount and when pagination/filters change
	useEffect(() => {
		fetchProjects();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current, pagination.pageSize, filterBy]);

	// Fetch business units from API
	const fetchBusinessUnits = async () => {
		setLoadingBusinessUnits(true);
		try {
			const result = await dispatch(searchBusinessUnitsV2({ limit: 1000 }));
			if (result?.results) {
				setBusinessUnits(result.results);
			}
		} catch (error) {
			// Error handled silently
		} finally {
			setLoadingBusinessUnits(false);
		}
	};

	const fetchClients = async () => {
		setLoadingClients(true);
		try {
			const result = await dispatch(searchClientFiltersV2({ limit: 100 }));
			if (result?.results) {
				setClients(result.results);
			}
		} catch (error) {
			// Error handled silently
		} finally {
			setLoadingClients(false);
		}
	};
	const fetchRouteOptions = useCallback(
		async (searchTerm = '', selectedRouteId = null) => {
			setLoadingRoutes(true);
			try {
				const searchRes = await dispatch(
					searchRouteFiltersV2({
						filter_by: searchTerm ? { query_str: searchTerm } : {},
						limit: 1000,
					})
				);
				const searchOptions = searchRes?.results || [];

				let selectedRoute = null;
				if (
					selectedRouteId &&
					!searchOptions.some((option) => option.value === selectedRouteId)
				) {
					selectedRoute = await dispatch(getRouteByIdV2({ id: selectedRouteId }));
				}

				setRouteOptions(
					mergeRouteOptions(searchOptions, selectedRoute ? [selectedRoute] : [])
				);
			} catch (error) {
				// Error handled silently
			} finally {
				setLoadingRoutes(false);
			}
		},
		[dispatch]
	);

	const handleRouteSearch = useCallback(
		(value) => {
			if (routeSearchTimeoutRef.current) {
				clearTimeout(routeSearchTimeoutRef.current);
			}
			routeSearchTimeoutRef.current = setTimeout(() => {
				fetchRouteOptions(value, routeFilter);
			}, 300);
		},
		[fetchRouteOptions, routeFilter]
	);

	const handleRouteChange = (value, option) => {
		setRouteFilter(value ?? null);
		setSelectedRouteLabel(value ? option?.label || '' : '');
	};

	const fetchProjects = async () => {
		setLoading(true);
		try {
			const result = await dispatch(
				getProjectListV2({
					filter_by: filterBy,
					pagination: {
						page: pagination.current,
						per_page: pagination.pageSize,
					},
				})
			);

			if (result) {
				const tableData = (result.rows || []).map(mapProjectToTableRow);
				setProjects(tableData);
				setPagination((prev) => ({
					...prev,
					total: result.count || 0,
				}));
			}
		} catch (error) {
			// Error handled silently
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		const trimmedValue = value ? value.trim() : '';
		setSearchValue(trimmedValue);
		setFilterBy((prev) => ({
			...prev,
			search_term: trimmedValue || undefined,
		}));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleTableChange = (paginationConfig) => {
		// Update pagination
		setPagination({
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
			total: pagination.total,
		});
		// Note: V2 API currently doesn't support sorting via API
		// Sorting will be handled client-side by Ant Design Table
	};

	const handleExport = () => {
		const { search_term, business_unit_id, client_name, ...restFilters } = filterBy;
		const normalizedBusinessUnitFilter =
			selectedBusinessUnits.length > 1
				? selectedBusinessUnits
				: selectedBusinessUnits.length === 1
				? selectedBusinessUnits[0]
				: business_unit_id;
		const exportData = {
			...restFilters,
			...(search_term ? { query_str: search_term } : {}),
			...(client_name ? { client_name } : {}),
			...(Array.isArray(normalizedBusinessUnitFilter)
				? { business_unit_id: normalizedBusinessUnitFilter }
				: normalizedBusinessUnitFilter
				? { business_unit_id: normalizedBusinessUnitFilter }
				: {}),
			url_type: 'download',
		};

		dispatch(
			exportProjectCSV({
				data: exportData,
			})
		)
			.then(() => {
				if (selectedBusinessUnits.length > 0) {
					message.warning(
						'Export completed. Some records without Business Unit association were excluded'
					);
				} else {
					message.success('Export started successfully.');
				}
			})
			.catch(() => {
				message.error('Failed to export projects. Please try again.');
			});
	};

	const handleSync = () => {
		fetchProjects();
	};

	const handleCreate = () => {
		navigate('/projects/add');
	};

	// Status toggle handlers
	const handleStatusToggle = (projectId, projectName, isActive) => {
		setSelectedProject({ id: projectId, name: projectName });
		setNewStatus(!isActive);
		setStatusModalVisible(true);
	};

	const handleConfirmStatusChange = async () => {
		setStatusUpdateLoading(true);
		try {
			await dispatch(
				updateProjectV2({
					id: selectedProject.id,
					req_data: { is_active: newStatus },
				})
			);
			// Optimistically update the local state immediately
			setProjects((prevData) =>
				prevData.map((project) =>
					project.id === selectedProject.id ? { ...project, status: newStatus } : project
				)
			);
			message.success('Project status updated successfully');
			// Refresh the project list after successful update to ensure data consistency
			await fetchProjects();
		} catch (error) {
			message.error('Failed to update project status. Please try again.');
			// Revert optimistic update on error by refreshing
			await fetchProjects();
		} finally {
			setStatusUpdateLoading(false);
			setStatusModalVisible(false);
			setSelectedProject(null);
		}
	};

	const handleCancelStatusChange = () => {
		setStatusModalVisible(false);
		setSelectedProject(null);
		setNewStatus(false);
	};

	// Filter handlers
	const handleStatusChange = (status) => {
		setStatusFilters((prev) => {
			const updatedStatus = { ...prev, [status]: !prev[status] };

			// If "All" is checked, uncheck Active and In-Active
			if (status === 'all' && !prev.all) {
				return { all: true, active: false, inactive: false };
			}

			// If Active or In-Active is checked, uncheck "All"
			if ((status === 'active' || status === 'inactive') && !prev[status]) {
				return { ...updatedStatus, all: false };
			}

			return updatedStatus;
		});
	};

	const handleBusinessUnitChange = (buId) => {
		setSelectedBusinessUnits((prev) => {
			if (prev.includes(buId)) {
				return prev.filter((id) => id !== buId);
			}
			return [...prev, buId];
		});
	};

	// Auto-apply filters whenever filter state changes
	useEffect(() => {
		const newFilters = {};

		// Add search term if exists
		if (searchValue) {
			newFilters.search_term = searchValue;
		}

		if (appliedClientName) {
			newFilters.client_name = appliedClientName;
		}

		// Add status filter
		if (statusFilters.active && !statusFilters.inactive) {
			newFilters.is_active = true;
		} else if (!statusFilters.active && statusFilters.inactive) {
			newFilters.is_active = false;
		}

		// Add business unit filter: single selection stays scalar, multiple selections go as array
		if (selectedBusinessUnits.length > 0) {
			newFilters.business_unit_id =
				selectedBusinessUnits.length === 1
					? selectedBusinessUnits[0]
					: selectedBusinessUnits;
		}

		// Add date range filter (use ISO format for consistency)
		if (dateRange && dateRange[0]) {
			newFilters.created_from = moment(dateRange[0]).startOf('day').toISOString();
		}
		if (dateRange && dateRange[1]) {
			newFilters.created_to = moment(dateRange[1]).endOf('day').toISOString();
		}
		if (routeFilter) {
			newFilters.route_id = routeFilter;
		}

		setFilterBy(newFilters);
		setPagination((prev) => ({ ...prev, current: 1 }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statusFilters, selectedBusinessUnits, clientNameFilter, dateRange, routeFilter]);

	const handleResetFilters = () => {
		setDateRange(null);
		setStatusFilters({ all: false, active: false, inactive: false });
		setClientNameFilter('');
		setSelectedBusinessUnits([]);
		setRouteFilter(null);
		setSelectedRouteLabel('');
		fetchRouteOptions('', null);
		setFilterBy({});
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	// Get visible business units (limited or all)
	const visibleBusinessUnits = showAllBusinessUnits ? businessUnits : businessUnits.slice(0, 5);

	// Date range handler
	const handleDateRangeChange = (dates) => {
		setDateRange(dates);
	};

	const handleToggleSeeMore = () => {
		setShowAllBusinessUnits(!showAllBusinessUnits);
	};

	// Calculate active filters count (excluding search term)
	const activeFiltersCount = [
		dateRange && dateRange[0],
		dateRange && dateRange[1],
		statusFilters.active || statusFilters.inactive,
		appliedClientName,
		selectedBusinessUnits.length > 0,
		routeFilter,
	].filter(Boolean).length;

	// Clear all filters
	const handleClearFilters = () => {
		setSearchValue('');
		setDateRange(null);
		setStatusFilters({ all: false, active: false, inactive: false });
		setClientNameFilter('');
		setSelectedBusinessUnits([]);
		setRouteFilter(null);
		setSelectedRouteLabel('');
		fetchRouteOptions('', null);
		setFilterBy({});
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	// Handlers to remove individual filters
	const handleRemoveDateFilter = () => setDateRange(null);
	const handleRemoveStatusFilter = () =>
		setStatusFilters({ all: false, active: false, inactive: false });
	const handleRemoveClientFilter = () => setClientNameFilter('');
	const handleRemoveBusinessUnitFilter = () => setSelectedBusinessUnits([]);
	const handleRemoveRouteFilter = () => {
		setRouteFilter(null);
		setSelectedRouteLabel('');
		fetchRouteOptions('', null);
	};

	return (
		<Box>
			<Flex justifyContent="space-between" mt={3}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<StyledFilterButton onClick={() => setFilterDrawerOpen(true)}>
						FILTERS {activeFiltersCount > 0 && `(${activeFiltersCount})`}
					</StyledFilterButton>
					<FilterDisplayBox>
						{dateRange && dateRange[0] && (
							<FilterTag onClose={handleRemoveDateFilter}>Date</FilterTag>
						)}
						{(statusFilters.active || statusFilters.inactive) && (
							<FilterTag onClose={handleRemoveStatusFilter}>Status</FilterTag>
						)}
						{appliedClientName && (
							<FilterTag onClose={handleRemoveClientFilter}>Client</FilterTag>
						)}
						{selectedBusinessUnits.length > 0 && (
							<FilterTag onClose={handleRemoveBusinessUnitFilter}>
								Business Unit
							</FilterTag>
						)}
						{routeFilter && (
							<FilterTag onClose={handleRemoveRouteFilter}>
								{`Route: ${selectedRouteLabel || routeFilter}`}
							</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleClearFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper
						onClick={handleExport}
						icons={<ExportOutlined />}
						loading={loading}
					>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />} loading={loading}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>

			<Box mt={3}>
				<AntdTable
					rowKey="id"
					columns={getTableColumns(navigate, handleStatusToggle)}
					dataSource={projects}
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						pageSizeOptions: ['10', '20', '50', '100'],
					}}
					onChange={handleTableChange}
				/>
			</Box>

			<Drawer
				className="userList-drawer"
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
				title={null}
				headerStyle={{ display: 'none' }}
			>
				<Box>
					{/* Filter Header */}
					<FilterHeader>
						<FilterTitleText>Filters</FilterTitleText>
						<FilterCloseButton onClick={() => setFilterDrawerOpen(false)} />
					</FilterHeader>

					{/* Date Range Section */}
					<FilterSection>
						<FilterSectionTitle>Date Range</FilterSectionTitle>
						<DateRangeWrapper>
							<StyledRangePicker
								placeholder={['Start Date', 'End Date']}
								value={dateRange}
								onChange={handleDateRangeChange}
								format="DD/MM/YYYY"
							/>
						</DateRangeWrapper>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Status Section */}
					<FilterSection>
						<FilterSectionTitle>Status</FilterSectionTitle>
						<CheckboxWrapper>
							<StyledCheckbox
								checked={statusFilters.all}
								onChange={() => handleStatusChange('all')}
							>
								All
							</StyledCheckbox>
							<StyledCheckbox
								checked={statusFilters.active}
								onChange={() => handleStatusChange('active')}
							>
								Active
							</StyledCheckbox>
							<StyledCheckbox
								checked={statusFilters.inactive}
								onChange={() => handleStatusChange('inactive')}
							>
								In-Active
							</StyledCheckbox>
						</CheckboxWrapper>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Client Section */}
					<FilterSection>
						<FilterSectionTitle>Client</FilterSectionTitle>
						<Select
							allowClear
							showSearch
							style={{ width: '100%' }}
							placeholder="Select or type client name"
							value={clientNameFilter || undefined}
							options={clients}
							onChange={(value, option) =>
								setClientNameFilter(option?.label || value || '')
							}
							filterOption={(inputValue, option) =>
								(option?.label || '')
									.toLowerCase()
									.includes(inputValue.toLowerCase())
							}
						/>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />

					{/* Business Unit Section */}
					<FilterSection>
						<FilterSectionTitle>Business Unit</FilterSectionTitle>
						<CheckboxWrapper>
							{loadingBusinessUnits && (
								<Text style={{ fontSize: '14px', color: '#999' }}>
									Loading business units...
								</Text>
							)}
							{!loadingBusinessUnits && visibleBusinessUnits.length > 0 && (
								<>
									{visibleBusinessUnits.map((bu) => (
										<StyledCheckbox
											key={bu.id}
											checked={selectedBusinessUnits.includes(bu.id)}
											onChange={() => handleBusinessUnitChange(bu.id)}
										>
											{bu.label}
										</StyledCheckbox>
									))}
									{businessUnits.length > 5 && (
										<SeeMoreLink onClick={handleToggleSeeMore}>
											<DownOutlined
												style={{
													transform: showAllBusinessUnits
														? 'rotate(180deg)'
														: 'rotate(0deg)',
													transition: 'transform 0.2s',
												}}
											/>
											{showAllBusinessUnits ? 'See less' : 'See more'}
										</SeeMoreLink>
									)}
								</>
							)}
							{!loadingBusinessUnits && visibleBusinessUnits.length === 0 && (
								<Text style={{ fontSize: '14px', color: '#999' }}>
									No business units available
								</Text>
							)}
						</CheckboxWrapper>
					</FilterSection>

					<Divider style={{ margin: '16px 0' }} />
					<FilterSection>
						<FilterSectionTitle>Route</FilterSectionTitle>
						<Select
							allowClear
							showSearch
							style={{ width: '100%' }}
							placeholder="Search route by name or code"
							value={routeFilter || undefined}
							options={routeOptions}
							filterOption={false}
							onSearch={handleRouteSearch}
							onChange={handleRouteChange}
							onClear={() => {
								handleRemoveRouteFilter();
								fetchRouteOptions('', null);
							}}
							loading={loadingRoutes}
							notFoundContent={
								loadingRoutes ? (
									<Text style={{ fontSize: '14px', color: '#999' }}>
										Searching...
									</Text>
								) : null
							}
						/>
					</FilterSection>

					{/* Action Buttons */}
					<Box mt={3}>
						<ResetButton onClick={handleResetFilters}>Reset Filters</ResetButton>
					</Box>
				</Box>
			</Drawer>

			{/* Status Confirmation Modal */}
			<StatusChangeModal
				visible={statusModalVisible}
				entityId={selectedProject?.name}
				entityName="project"
				newStatus={newStatus}
				onConfirm={handleConfirmStatusChange}
				onCancel={handleCancelStatusChange}
				loading={statusUpdateLoading}
			/>
		</Box>
	);
}
